/**
 * Dictation Exercise
 * User listens to audio and types what they hear.
 * Scoring is word-by-word comparison with acceptable variants.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { TouchableOpacity, TextInput, View } from 'react-native';
import { Box, Text, VStack, HStack } from '@gluestack-ui/themed';
import { colors, FS_XS, FS_SM } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface DictationExerciseProps {
  prompt: string;
  audioText: string;
  acceptableVariants?: string[];
  hint?: string;
  wordCount?: number;
  speed?: 'slow' | 'normal' | 'fast';
  onComplete: (correct: boolean, score: number) => void;
}

interface WordDiff {
  word: string;
  correct: boolean;
}

export const DictationExercise = React.memo(function DictationExercise({
  prompt,
  audioText,
  acceptableVariants,
  hint,
  wordCount,
  speed,
  onComplete,
}: DictationExerciseProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();

  const [userInput, setUserInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [wordDiffs, setWordDiffs] = useState<WordDiff[]>([]);

  const expectedWords = useMemo(() => {
    return audioText.toLowerCase().replace(/[.,!?;:]/g, '').trim().split(/\s+/);
  }, [audioText]);

  const totalWords = wordCount || expectedWords.length;

  const currentWordCount = useMemo(() => {
    const trimmed = userInput.trim();
    if (trimmed.length === 0) return 0;
    return trimmed.split(/\s+/).length;
  }, [userInput]);

  const allAcceptable = useMemo(() => {
    const variants = [audioText, ...(acceptableVariants || [])];
    return variants.map((v) =>
      v.toLowerCase().replace(/[.,!?;:]/g, '').trim().split(/\s+/)
    );
  }, [audioText, acceptableVariants]);

  const handleSubmit = useCallback(() => {
    if (submitted || userInput.trim().length === 0) return;

    const userWords = userInput.toLowerCase().replace(/[.,!?;:]/g, '').trim().split(/\s+/);

    // Find best matching variant
    let bestScore = 0;
    let bestDiffs: WordDiff[] = [];

    for (const variantWords of allAcceptable) {
      let correctCount = 0;
      const diffs: WordDiff[] = [];

      const maxLen = Math.max(userWords.length, variantWords.length);
      for (let i = 0; i < maxLen; i++) {
        const userWord = userWords[i] || '';
        const expectedWord = variantWords[i] || '';
        const isCorrect = userWord === expectedWord;
        if (isCorrect) correctCount++;
        diffs.push({ word: userWords[i] || `[${expectedWord}]`, correct: isCorrect });
      }

      const variantScore = Math.round((correctCount / variantWords.length) * 100);
      if (variantScore > bestScore) {
        bestScore = variantScore;
        bestDiffs = diffs;
      }
    }

    setScore(bestScore);
    setWordDiffs(bestDiffs);
    setSubmitted(true);
    onComplete(bestScore >= 80, bestScore);
  }, [submitted, userInput, allAcceptable, onComplete]);

  const speedLabel = speed === 'slow' ? '0.75x' : speed === 'fast' ? '1.5x' : '1x';

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
    >
      <VStack space="md">
        {/* Prompt */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
        >
          {prompt}
        </Text>

        {/* Audio play area */}
        <Box
          p="$4"
          rounded="$lg"
          alignItems="center"
          style={{ backgroundColor: theme.primarySoft }}
        >
          <HStack space="md" alignItems="center">
            <Text allowFontScaling={false} style={{ fontSize: 32 }}>🔊</Text>
            <VStack>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.primary, ...ffs }}
              >
                Listen and type
              </Text>
              {speed && (
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: theme.primary, ...ffs }}
                >
                  Speed: {speedLabel}
                </Text>
              )}
            </VStack>
          </HStack>
        </Box>

        {hint && !submitted && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Text input */}
        {!submitted && (
          <TextInput
            testID="dictation-input"
            accessibilityLabel="Type what you hear"
            accessibilityRole="text"
            value={userInput}
            onChangeText={setUserInput}
            placeholder="Type what you hear..."
            placeholderTextColor={theme.textSecondary}
            multiline
            returnKeyType="done"
            onSubmitEditing={handleSubmit}
            style={{
              backgroundColor: theme.inputBg,
              borderWidth: 1,
              borderColor: theme.border,
              borderRadius: 8,
              padding: 12,
              fontSize: FS_SM * fontScale,
              color: theme.text,
              minHeight: 80,
              textAlignVertical: 'top',
              ...ffs,
            }}
            allowFontScaling={false}
          />
        )}

        {/* Word counter */}
        {!submitted && (
          <Text
            allowFontScaling={false}
            style={{
              fontSize: FS_XS * fontScale,
              color: currentWordCount >= totalWords ? theme.primary : theme.textSecondary,
              textAlign: 'right',
              ...ffs,
            }}
          >
            {currentWordCount}/{totalWords} words
          </Text>
        )}

        {/* Submit button */}
        {!submitted && userInput.trim().length > 0 && (
          <TouchableOpacity
            testID="dictation-submit"
            accessibilityLabel="Submit answer"
            accessibilityRole="button"
            onPress={handleSubmit}
            activeOpacity={0.7}
          >
            <Box
              py="$3"
              rounded="$md"
              alignItems="center"
              style={{ backgroundColor: colors.primary }}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#FFFFFF', ...ffs }}
              >
                Check Answer
              </Text>
            </Box>
          </TouchableOpacity>
        )}

        {/* Word diff result */}
        {submitted && (
          <VStack space="sm">
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
              {wordDiffs.map((diff, idx) => (
                <Box
                  key={idx}
                  px="$2"
                  py="$1"
                  rounded="$sm"
                  style={{ backgroundColor: diff.correct ? '#D1FAE5' : '#FEE2E2' }}
                >
                  <Text
                    allowFontScaling={false}
                    style={{
                      fontSize: FS_SM * fontScale,
                      fontWeight: '500',
                      color: diff.correct ? '#065F46' : '#991B1B',
                      ...ffs,
                    }}
                  >
                    {diff.word}
                  </Text>
                </Box>
              ))}
            </View>

            {/* Score and correct answer */}
            <Box
              p="$2"
              rounded="$md"
              style={{ backgroundColor: score >= 80 ? '#D1FAE5' : '#FEE2E2' }}
            >
              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_SM * fontScale,
                  fontWeight: '600',
                  color: score >= 80 ? '#065F46' : '#991B1B',
                  ...ffs,
                }}
              >
                {score >= 80 ? `✓ Score: ${score}%` : `✗ Score: ${score}%`}
              </Text>
              {score < 100 && (
                <Text
                  allowFontScaling={false}
                  style={{
                    fontSize: FS_XS * fontScale,
                    color: theme.textSecondary,
                    marginTop: 4,
                    ...ffs,
                  }}
                >
                  Correct: {audioText}
                </Text>
              )}
            </Box>
          </VStack>
        )}
      </VStack>
    </Box>
  );
});
